/**
 * PURPOSE: Modal "Asignar camisetas" de Programación (FTR-0041, FTR-0042, FTR-0043, FTR-0044, FTR-0046).
 * RESPONSIBILITIES: Flujo PLANIFICAR → VISTA PREVIA → REVISAR → APLICAR. Planificar: la fase (todas —por defecto—,
 *   la de grupos o una etapa final; las fases salen de la vista previa), qué uniformes (camisetas
 *   principales, alternos del deporte o ambos), el modo (completar faltantes —por defecto, conserva lo existente—
 *   o recalcular) y los partidos (todos los elegibles, solo los que tienen faltantes o una selección). La vista
 *   previa la calcula el backend (nunca escribe) y se revisa con un resumen por clase, advertencias (fuertes,
 *   reglas de color, excepciones a la alternancia, alternos del mismo color), pendientes y una tarjeta por
 *   partido con la camiseta y el alterno de cada lado (antes → después, explicación y comprobaciones), con
 *   filtros. Aplicar exige confirmar (nombra cada categoría) y envía el planHash de esa vista; si la programación
 *   cambió (409) se recalcula. Con el versionamiento del evento activo (FTR-0046) muestra en qué versión se trabaja
 *   (lo aplicado queda en esa versión) y la pestaña Versiones permite usar otra, crear una copia o eliminar una que
 *   no esté en uso; sin él, no hay pestañas. No contiene el algoritmo.
 * COLLABORATORS: uniformOptionsService.js (previewAutoUniforms/applyAutoUniforms), uniformAutoAssignDisplay.js,
 *   useUniformVersions.js, UniformVersionsPanel.jsx, UniformOptionVisual.jsx, ProgramacionArea.jsx (solo los roles 1 y
 *   4 lo abren; el backend también lo exige).
 * ROUTE: winscore/src/area/ProgramacionArea/AssignUniformsModal.jsx
 */
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { TbShirtFilled } from "react-icons/tb";
import { toast } from "react-hot-toast";

import UniformOptionVisual from "../../admin/AdminCompany/UniformOptionVisual.jsx";
import { getUniformErrorMessage } from "../../admin/AdminCompany/uniformOptions.utils.js";
import { applyAutoUniforms, previewAutoUniforms } from "../../services/uniformOptionsService.js";
import {
  ACTION_LABELS,
  ACTION_TONES,
  ALL_PHASES_KEY,
  AUTO_SCOPES,
  BOTH_KINDS,
  CARD_FILTERS,
  CARD_FILTER_LABELS,
  CRITICAL_REPEAT_CODE,
  MANUAL_ONLY_REASONS,
  NOT_ELIGIBLE_TEXT,
  SAME_TONE_CODE,
  alternatePlural,
  buildAutoAssignBody,
  buildMatchCards,
  confirmationText,
  currentText,
  filterMatchCards,
  groupEligibleMatches,
  isInPhase,
  kindSummaryRows,
  kindText,
  modeHint,
  phaseFromKey,
  phaseHint,
  phaseKeyOf,
  phaseSelectOptions,
  proposedText,
  splitWarnings,
  summaryItems,
} from "../../utils/uniformAutoAssignDisplay.js";
import UniformVersionsPanel from "./UniformVersionsPanel.jsx";
import { useUniformVersions } from "../../hooks/useUniformVersions.js";
import { versionTitle } from "../../utils/uniformVersionsDisplay.js";

const TONE_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};
const SEVERITY_CLASSES = {
  STRONG: "text-rose-800",
  WARNING: "text-amber-900",
  INFO: "text-slate-600",
};
const TABS = Object.freeze({ ASSIGN: "ASSIGN", VERSIONS: "VERSIONS" });

const OptionCell = ({ option, text, muted = false }) => (
  <span className="inline-flex min-w-0 items-center gap-1.5">
    {option ? <UniformOptionVisual option={option} size="xs" swatchSize="sm" showPlaceholder={false} alt={text} /> : null}
    <span className={`truncate ${muted ? "text-slate-500 line-through decoration-slate-300" : ""}`}>{text}</span>
  </span>
);

const ActionBadge = ({ action }) => (
  <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${TONE_CLASSES[ACTION_TONES[action]] || TONE_CLASSES.slate}`}>
    {ACTION_LABELS[action] || action}
  </span>
);

/** Camiseta o alterno de un lado: antes → después, explicación y comprobaciones (las fallidas siempre visibles). */
const SlotRow = ({ entry, exclusiveStrategy }) => {
  const [open, setOpen] = useState(false);
  const kindName = kindText(entry.kind, exclusiveStrategy);
  const showsChange = entry.action === "NEW" || entry.action === "MODIFIED";
  const hasCurrent = entry.current && entry.current.status !== "EMPTY";
  const failed = (entry.checks || []).filter((check) => !check.ok);
  const explainable = Boolean(entry.reason) || (entry.checks || []).length > 0;
  return (
    <div role="group" className="rounded-lg bg-slate-50 p-2 ring-1 ring-slate-200" aria-label={`${kindName} de ${entry.institution?.name || "por definir"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{kindName}</span>
        <ActionBadge action={entry.action} />
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-slate-800">
        {showsChange ? (
          <>
            {hasCurrent ? (
              <>
                <OptionCell option={entry.current?.status === "OPTION" ? entry.current.option : null} text={currentText(entry.current)} muted />
                <span className="text-slate-400">→</span>
              </>
            ) : null}
            <OptionCell option={entry.proposed} text={proposedText(entry)} />
          </>
        ) : (
          <OptionCell
            option={entry.current?.status === "OPTION" ? entry.current.option : null}
            text={hasCurrent ? currentText(entry.current) : "Sin asignar"}
          />
        )}
      </div>
      {entry.action === "UNASSIGNABLE" && entry.reason ? <p className="mt-1 text-xs font-semibold text-rose-700">{entry.reason}</p> : null}
      {failed.length && entry.action !== "UNASSIGNABLE" ? (
        <ul className="mt-1 space-y-0.5 text-xs font-semibold text-rose-700">
          {failed.map((check) => (
            <li key={check.key}>✗ {check.text}</li>
          ))}
        </ul>
      ) : null}
      {explainable && entry.action !== "UNASSIGNABLE" ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="mt-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900"
        >
          {open ? "Ocultar explicación" : "Ver explicación"}
        </button>
      ) : null}
      {open ? (
        <div className="mt-1 rounded-md bg-white p-2 text-xs text-slate-700 ring-1 ring-slate-200">
          {entry.reason ? <p>{entry.reason}</p> : null}
          {entry.checks?.length ? (
            <ul className="mt-1 space-y-0.5">
              {entry.checks.map((check) => (
                <li key={check.key} className={check.ok ? "text-emerald-700" : "font-semibold text-rose-700"}>
                  {check.ok ? "✓" : "✗"} {check.text}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const MatchCard = ({ card, exclusiveStrategy }) => (
  <article aria-label={card.label} className="rounded-xl border border-slate-200 bg-white shadow-sm">
    <header className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 px-3 py-2">
      <div className="min-w-0">
        <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">{card.stage}</p>
        <h3 className="truncate text-sm font-bold text-slate-900">{card.label}</h3>
      </div>
      <div className="flex flex-wrap gap-1">
        {card.changed ? <span className={`rounded-full px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${TONE_CLASSES.emerald}`}>Con cambios</span> : null}
        {card.unassigned ? <span className={`rounded-full px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${TONE_CLASSES.rose}`}>Sin asignar</span> : null}
        {card.warned ? (
          <span className={`rounded-full px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${card.strong ? TONE_CLASSES.rose : TONE_CLASSES.amber}`}>
            ⚠ {card.warnings.length}
          </span>
        ) : null}
      </div>
    </header>
    <div className="grid gap-3 p-3 md:grid-cols-2">
      {card.sides.map((side) => (
        <div key={side.side} className="space-y-2">
          <p className="text-xs font-black uppercase text-slate-700">
            {side.side === 1 ? "Local" : "Visitante"} · <span className="normal-case">{side.institution?.name || "Por definir"}</span>
          </p>
          {side.MAIN ? <SlotRow entry={side.MAIN} exclusiveStrategy={exclusiveStrategy} /> : null}
          {side.EXCLUSIVE ? <SlotRow entry={side.EXCLUSIVE} exclusiveStrategy={exclusiveStrategy} /> : null}
        </div>
      ))}
    </div>
    {card.warnings.length ? (
      <ul className="space-y-0.5 border-t border-slate-100 px-3 py-2 text-xs">
        {card.warnings.map((warning, index) => (
          <li key={`${warning.code}-${index}`} className={SEVERITY_CLASSES[warning.severity] || SEVERITY_CLASSES.INFO}>
            {warning.severity === "STRONG" ? "⚠⚠" : warning.severity === "WARNING" ? "⚠" : "ℹ"} {warning.message}
          </li>
        ))}
      </ul>
    ) : null}
  </article>
);

const WarningList = ({ items }) => (
  <ul className="mt-1 space-y-1 text-sm">
    {items.map((item, index) => (
      <li key={`${item.code}-${index}`}>• {item.message}</li>
    ))}
  </ul>
);

export default function AssignUniformsModal({ open, onClose, idevent, idsport, onApplied }) {
  const [tab, setTab] = useState(TABS.ASSIGN);
  const [scopeMode, setScopeMode] = useState(AUTO_SCOPES.ALL_ELIGIBLE);
  const [selectedIds, setSelectedIds] = useState([]);
  const [recalculate, setRecalculate] = useState(false);
  const [kinds, setKinds] = useState(BOTH_KINDS);
  // Fase a trabajar: todas (por defecto, el comportamiento de siempre), la de grupos o una etapa final.
  const [phaseKey, setPhaseKey] = useState(ALL_PHASES_KEY);
  const [preview, setPreview] = useState(null);
  const [previewBody, setPreviewBody] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const [cardFilter, setCardFilter] = useState(CARD_FILTERS.ALL);
  const [search, setSearch] = useState("");
  const requestRef = useRef(0);
  // FTR-0046: las versiones de uniformes se gestionan solo aquí (pestaña Versiones); se cargan al abrir.
  const versions = useUniformVersions({ idevent, idsport, enabled: open });
  const showVersions = versions.visible && Boolean(versions.data.active);

  const runPreview = useCallback(
    async ({ mode = scopeMode, ids = selectedIds, recalc = recalculate, kindsValue = kinds, phaseKeyValue = phaseKey } = {}) => {
      if (mode === AUTO_SCOPES.SELECTED && ids.length === 0) {
        setPreview((current) => (current ? { ...current, entries: [], warnings: [], pending: [], notEligible: [] } : current));
        setPreviewBody(null);
        setStale(false);
        return;
      }
      const body = buildAutoAssignBody({
        idevent,
        idsport,
        scopeMode: mode,
        selectedIds: ids,
        recalculate: recalc,
        kinds: kindsValue,
        phase: phaseFromKey(phaseKeyValue),
      });
      const requestId = ++requestRef.current;
      setLoading(true);
      setError("");
      setConfirming(false);
      try {
        const data = await previewAutoUniforms(body);
        if (requestId !== requestRef.current) return;
        setPreview(data);
        setPreviewBody(body);
        setStale(false);
      } catch (err) {
        if (requestId !== requestRef.current) return;
        setError(getUniformErrorMessage(err, "No se pudo calcular la propuesta."));
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [idevent, idsport, scopeMode, selectedIds, recalculate, kinds, phaseKey]
  );

  // Al abrir: estado inicial y propuesta por defecto (ambos uniformes, completar faltantes, todas las fases, todos los
  // elegibles).
  useEffect(() => {
    if (!open) return;
    setTab(TABS.ASSIGN);
    setScopeMode(AUTO_SCOPES.ALL_ELIGIBLE);
    setSelectedIds([]);
    setRecalculate(false);
    setKinds(BOTH_KINDS);
    setPhaseKey(ALL_PHASES_KEY);
    setPreview(null);
    setPreviewBody(null);
    setShowWarnings(false);
    setCardFilter(CARD_FILTERS.ALL);
    setSearch("");
    runPreview({ mode: AUTO_SCOPES.ALL_ELIGIBLE, ids: [], recalc: false, kindsValue: BOTH_KINDS, phaseKeyValue: ALL_PHASES_KEY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const changeScope = (mode) => {
    setScopeMode(mode);
    if (mode === AUTO_SCOPES.SELECTED) setStale(true);
    else runPreview({ mode, ids: [] });
  };

  // Otra fase: con "Seleccionar partidos" la lista se limita a esa fase (la selección se rehace); si no, se recalcula.
  const changePhase = (key) => {
    setPhaseKey(key);
    if (scopeMode === AUTO_SCOPES.SELECTED) {
      setSelectedIds([]);
      setStale(true);
    } else {
      runPreview({ phaseKeyValue: key });
    }
  };

  const changeRecalculate = (value) => {
    setRecalculate(value);
    runPreview({ recalc: value });
  };

  const toggleKind = (key) => {
    const next = { ...kinds, [key]: !kinds[key] };
    if (!next.main && !next.alternate) return;
    setKinds(next);
    runPreview({ kindsValue: next });
  };

  const toggleSelected = (idmatch) => {
    setSelectedIds((current) => (current.includes(idmatch) ? current.filter((id) => id !== idmatch) : [...current, idmatch]));
    setStale(true);
  };

  const toggleGroup = (matches) => {
    const ids = matches.map((match) => match.idmatch);
    setSelectedIds((current) => {
      const allOn = ids.every((id) => current.includes(id));
      return allOn ? current.filter((id) => !ids.includes(id)) : [...new Set([...current, ...ids])];
    });
    setStale(true);
  };

  const summary = preview?.summary || {};
  const writes = summary.writes ?? 0;
  const exclusiveStrategy = preview?.sport?.exclusiveStrategy || null;
  const workedKinds = preview?.uniforms || kinds;
  const { strong, conflicts, uniformRepeats, toneRepeats, colorRules, alternatePairs, otherWarnings, info } = splitWarnings(preview?.warnings || []);
  const confirmation = confirmationText({
    strongCount: strong.length,
    sameToneCount: strong.filter((item) => item.code === SAME_TONE_CODE).length,
    criticalCount: strong.filter((item) => item.code === CRITICAL_REPEAT_CODE).length,
    colorRuleCount: colorRules.filter((item) => item.severity === "STRONG").length,
    uniformRepeatCount: uniformRepeats.length,
    toneRepeatCount: toneRepeats.length,
    alternatePairCount: alternatePairs.length,
    writes,
  });
  const canApply = Boolean(preview && previewBody && !stale && !loading && !applying && writes > 0);

  const handleApply = async () => {
    if (!canApply) return;
    setApplying(true);
    try {
      const result = await applyAutoUniforms({
        ...previewBody,
        planHash: preview.planHash,
        ...(strong.length ? { confirmStrongWarnings: true } : {}),
      });
      const where = showVersions ? ` Quedó en ${versionTitle(versions.data.active)}.` : "";
      toast.success(`Camisetas asignadas en ${result.appliedMatches ?? 0} partido(s).${where}`);
      onApplied?.(result);
      onClose?.();
    } catch (err) {
      const code = err?.response?.data?.code;
      toast.error(getUniformErrorMessage(err, "No se aplicó ningún cambio."));
      setConfirming(false);
      // La programación cambió: nada se escribió; se recalcula para que el usuario revise la nueva propuesta.
      if (code === "UNIFORM_PLAN_CHANGED" || code === "CONFIRMATION_REQUIRED") runPreview();
    } finally {
      setApplying(false);
    }
  };

  // Otra versión en uso (o una copia nueva): los partidos cambian; se recargan y se recalcula la propuesta.
  const handleVersionChanged = (result) => {
    onApplied?.(result);
    runPreview();
  };

  const cards = buildMatchCards(preview?.entries || [], preview?.warnings || []);
  const visibleCards = filterMatchCards(cards, { filter: cardFilter, search });
  const filterCount = (filter) => filterMatchCards(cards, { filter }).length;
  const phase = phaseFromKey(phaseKey);
  const phaseSummary = phase ? (preview?.phases || []).find((item) => phaseKeyOf(item) === phaseKey) || null : null;
  const eligibleGroups = groupEligibleMatches((preview?.eligibleMatches || []).filter((match) => isInPhase(match, phase)));
  const eligibleCount = phase ? phaseSummary?.eligible ?? 0 : summary.eligibleMatches ?? 0;
  const kindRows = kindSummaryRows(summary, { uniforms: workedKinds, exclusiveStrategy });
  const alternates = alternatePlural(exclusiveStrategy);

  const radio = (name, checked, onChange, label, extra = null) => (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
      <input type="radio" name={name} checked={checked} onChange={onChange} disabled={applying} />
      <span>
        {label}
        {extra}
      </span>
    </label>
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={() => (applying ? null : onClose?.())}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-[71] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-150 transform"
              enterFrom="opacity-0 scale-95 translate-y-1"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-100 transform"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-1"
            >
              <Dialog.Panel className="flex max-h-[92vh] w-full max-w-6xl flex-col rounded-2xl bg-white text-left shadow-xl ring-1 ring-black/5">
                <div className="flex items-start gap-3 border-b border-slate-100 p-5 pb-3">
                  <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 ring-1 ring-inset ring-indigo-200">
                    <TbShirtFilled className="h-5 w-5 text-indigo-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Dialog.Title className="text-base font-semibold text-gray-900">Asignar camisetas</Dialog.Title>
                    <p className="mt-0.5 text-sm text-gray-600">
                      Propuesta para todo el torneo a la vez o para una fase. Nunca deja el mismo color en ambos equipos ni la
                      camiseta y el alterno de un equipo del mismo color; evita que el alterno coincida con la camiseta rival y
                      repetir uniformes en partidos seguidos. En fase de grupos solo cambian partidos por jugarse y no
                      publicados; en las fases finales solo los que no empezaron, estén publicados o no. Los partidos en vivo,
                      finalizados u oficializados se corrigen a mano, partido por partido.
                    </p>
                    {showVersions ? (
                      <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-violet-50 px-3 py-0.5 text-xs font-semibold text-violet-900 ring-1 ring-violet-200">
                        Trabajando en {versionTitle(versions.data.active)}: lo que apliques queda en esta versión.
                      </p>
                    ) : null}
                    {showVersions ? (
                    <div role="tablist" aria-label="Secciones" className="mt-3 flex gap-1">
                      {[
                        { key: TABS.ASSIGN, label: "Asignación automática" },
                        { key: TABS.VERSIONS, label: `Versiones (${versions.data.versions.length})` },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          role="tab"
                          aria-selected={tab === item.key}
                          onClick={() => setTab(item.key)}
                          disabled={applying}
                          className={`rounded-t-lg px-3 py-1.5 text-sm font-semibold ${
                            tab === item.key ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                    ) : null}
                  </div>
                </div>

                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
                  {tab === TABS.VERSIONS && showVersions ? (
                    <UniformVersionsPanel versions={versions} disabled={applying} onChanged={handleVersionChanged} />
                  ) : (
                    <>
                      {/* 1 · Planificar */}
                      <div className="grid gap-3 rounded-xl border border-slate-200 p-3 md:grid-cols-2 xl:grid-cols-4">
                        <fieldset className="flex flex-col gap-1">
                          <legend className="mb-1 text-xs font-black uppercase tracking-wide text-slate-500">Fase</legend>
                          <select
                            aria-label="Fase"
                            value={phaseKey}
                            onChange={(event) => changePhase(event.target.value)}
                            disabled={applying}
                            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800"
                          >
                            {phaseSelectOptions(preview?.phases || [], phaseKey).map((option) => (
                              <option key={option.key || "all"} value={option.key}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <span className="text-xs text-slate-500">{phaseHint(phase, phaseSummary)}</span>
                        </fieldset>
                        {exclusiveStrategy ? (
                          <fieldset className="flex flex-col gap-1">
                            <legend className="mb-1 text-xs font-black uppercase tracking-wide text-slate-500">Uniformes</legend>
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={kinds.main}
                                onChange={() => toggleKind("main")}
                                disabled={applying || (kinds.main && !kinds.alternate)}
                              />
                              <span>Camisetas principales</span>
                            </label>
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={kinds.alternate}
                                onChange={() => toggleKind("alternate")}
                                disabled={applying || (kinds.alternate && !kinds.main)}
                              />
                              <span>{alternates} (alternos)</span>
                            </label>
                          </fieldset>
                        ) : null}
                        <fieldset className="flex flex-col gap-1">
                          <legend className="mb-1 text-xs font-black uppercase tracking-wide text-slate-500">Modo</legend>
                          {radio("auto-uniform-mode", !recalculate, () => changeRecalculate(false), "Completar faltantes / pendientes")}
                          {radio("auto-uniform-mode", recalculate, () => changeRecalculate(true), "Recalcular asignaciones existentes")}
                          <span className="text-xs text-slate-500">{modeHint({ recalculate, kinds, exclusiveStrategy })}</span>
                        </fieldset>
                        <fieldset className="flex flex-col gap-1">
                          <legend className="mb-1 text-xs font-black uppercase tracking-wide text-slate-500">Partidos</legend>
                          {radio(
                            "auto-uniform-scope",
                            scopeMode === AUTO_SCOPES.ALL_ELIGIBLE,
                            () => changeScope(AUTO_SCOPES.ALL_ELIGIBLE),
                            phase ? "Todos los partidos elegibles de la fase" : "Todos los partidos elegibles",
                            preview ? <strong> ({eligibleCount})</strong> : null
                          )}
                          {radio("auto-uniform-scope", scopeMode === AUTO_SCOPES.MISSING, () => changeScope(AUTO_SCOPES.MISSING), "Solo partidos con faltantes")}
                          {radio("auto-uniform-scope", scopeMode === AUTO_SCOPES.SELECTED, () => changeScope(AUTO_SCOPES.SELECTED), "Seleccionar partidos")}
                        </fieldset>
                      </div>

                      {scopeMode === AUTO_SCOPES.SELECTED ? (
                        <div className="rounded-xl border border-slate-200 p-3">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
                              Partidos elegibles · {selectedIds.length} seleccionado(s)
                            </span>
                            <button
                              type="button"
                              onClick={() => runPreview()}
                              disabled={loading || applying || selectedIds.length === 0}
                              className="rounded-md bg-indigo-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                            >
                              Actualizar vista previa
                            </button>
                          </div>
                          <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                            {eligibleGroups.length === 0 ? (
                              <p className="text-sm text-slate-500">No hay partidos elegibles.</p>
                            ) : (
                              eligibleGroups.map((group) => (
                                <div key={group.key}>
                                  <label className="flex items-center gap-2 text-xs font-black uppercase text-slate-700">
                                    <input
                                      type="checkbox"
                                      checked={group.matches.every((match) => selectedIds.includes(match.idmatch))}
                                      onChange={() => toggleGroup(group.matches)}
                                      aria-label={`Seleccionar ${group.stage}`}
                                    />
                                    {group.stage}
                                  </label>
                                  <div className="ml-5 mt-1 grid gap-1 sm:grid-cols-2">
                                    {group.matches.map((match) => (
                                      <label key={match.idmatch} className="flex items-center gap-2 text-sm text-slate-700">
                                        <input
                                          type="checkbox"
                                          checked={selectedIds.includes(match.idmatch)}
                                          onChange={() => toggleSelected(match.idmatch)}
                                        />
                                        <span className="truncate">{match.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ) : null}

                      {error ? (
                        <div role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">
                          {error}
                        </div>
                      ) : null}

                      {loading ? <p className="text-sm font-semibold text-slate-500">Calculando propuesta…</p> : null}

                      {preview && !loading ? (
                        <>
                          {stale ? (
                            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200">
                              Cambiaste el alcance: actualiza la vista previa antes de aplicar.
                            </p>
                          ) : null}

                          {/* 2 · Vista previa: resumen por clase */}
                          <div className="space-y-1.5 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" aria-label="Resumen de la propuesta">
                            {kindRows.length ? (
                              <>
                                {kindRows.map((row) => (
                                  <div key={row.kind} className="flex flex-wrap items-center gap-2" aria-label={`Resumen ${row.title}`}>
                                    <span className="w-44 text-xs font-black uppercase tracking-wide text-slate-600">{row.title}</span>
                                    {row.items.map((item) => (
                                      <span
                                        key={item.key}
                                        className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-0.5 text-xs font-bold text-slate-700 ring-1 ring-slate-200"
                                      >
                                        {item.label} <span className="text-slate-900">{item.value}</span>
                                      </span>
                                    ))}
                                  </div>
                                ))}
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="w-44 text-xs font-black uppercase tracking-wide text-slate-600">Partidos</span>
                                  {[
                                    { key: "target", label: "En el alcance", value: summary.targetMatches ?? 0 },
                                    { key: "writes", label: "Cambios a guardar", value: writes },
                                    { key: "pending", label: "Pendientes", value: summary.pending ?? 0 },
                                    { key: "warnings", label: "Advertencias", value: (summary.warnings ?? 0) + (summary.strongWarnings ?? 0) },
                                  ].map((item) => (
                                    <span
                                      key={item.key}
                                      className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-0.5 text-xs font-bold text-slate-700 ring-1 ring-slate-200"
                                    >
                                      {item.label} <span className="text-slate-900">{item.value}</span>
                                    </span>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {summaryItems(summary).map((item) => (
                                  <span
                                    key={item.key}
                                    className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200"
                                  >
                                    {item.label} <span className="text-slate-900">{item.value}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* 3 · Revisar: advertencias */}
                          {strong.length ? (
                            <div role="alert" className="rounded-xl bg-rose-50 p-3 ring-1 ring-rose-300">
                              <p className="text-sm font-black text-rose-800">⚠⚠ Requieren confirmación ({strong.length})</p>
                              <div className="text-rose-800">
                                <WarningList items={strong} />
                              </div>
                            </div>
                          ) : null}

                          {conflicts.length ? (
                            <div role="status" className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-300">
                              <p className="text-sm font-black text-amber-900">
                                ⚠ Excepciones a la alternancia de camisetas ({conflicts.length})
                              </p>
                              {[
                                { key: "uniform", title: "Repiten el mismo uniforme", items: uniformRepeats },
                                { key: "tone", title: "Repiten tono con otra camiseta (menos grave)", items: toneRepeats },
                              ]
                                .filter((group) => group.items.length)
                                .map((group) => (
                                  <div key={group.key} className="mt-1 text-amber-900">
                                    <p className="text-xs font-bold uppercase tracking-wide text-amber-800">
                                      {group.title} ({group.items.length})
                                    </p>
                                    <WarningList items={group.items} />
                                  </div>
                                ))}
                            </div>
                          ) : null}

                          {alternatePairs.length ? (
                            <div role="status" className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-300">
                              <p className="text-sm font-black text-amber-900">
                                ⚠ {alternates} del mismo color en ambos equipos — último recurso ({alternatePairs.length})
                              </p>
                              <div className="text-amber-900">
                                <WarningList items={alternatePairs} />
                              </div>
                            </div>
                          ) : null}

                          {otherWarnings.length || info.length ? (
                            <div className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-200">
                              <button
                                type="button"
                                onClick={() => setShowWarnings((value) => !value)}
                                className="text-sm font-bold text-amber-900"
                                aria-expanded={showWarnings}
                              >
                                ⚠ Advertencias ({otherWarnings.length}) · Avisos ({info.length}) {showWarnings ? "▲" : "▼"}
                              </button>
                              {showWarnings ? (
                                <div className="text-amber-900">
                                  <WarningList items={[...otherWarnings, ...info]} />
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {preview.pending?.length ? (
                            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 ring-1 ring-slate-200">
                              <p className="font-bold">Pendiente / no resoluble todavía ({preview.pending.length})</p>
                              <ul className="mt-1 space-y-0.5">
                                {preview.pending.map((item) => (
                                  <li key={item.idmatch}>• {item.label}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}

                          {preview.notEligible?.length ? (
                            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 ring-1 ring-slate-200">
                              <p className="font-bold">Omitidos ({preview.notEligible.length})</p>
                              {preview.notEligible.some((item) => MANUAL_ONLY_REASONS.includes(item.reason)) ? (
                                <p className="mt-0.5 text-xs text-slate-600">
                                  En fases finales solo se asignan partidos no iniciados. Corrige estos a mano desde cada partido.
                                </p>
                              ) : null}
                              <ul className="mt-1 space-y-0.5">
                                {preview.notEligible.map((item) => (
                                  <li key={item.idmatch}>
                                    • {item.label || `Partido ${item.idmatch}`}: {NOT_ELIGIBLE_TEXT[item.reason] || item.reason}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}

                          {/* 3 · Revisar: un partido por tarjeta */}
                          <section aria-label="Detalle de la propuesta" className="space-y-3">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div role="group" aria-label="Filtrar partidos" className="flex flex-wrap gap-1">
                                {Object.values(CARD_FILTERS).map((filter) => (
                                  <button
                                    key={filter}
                                    type="button"
                                    aria-pressed={cardFilter === filter}
                                    onClick={() => setCardFilter(filter)}
                                    className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ring-inset ${
                                      cardFilter === filter ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
                                    }`}
                                  >
                                    {CARD_FILTER_LABELS[filter]} ({filterCount(filter)})
                                  </button>
                                ))}
                              </div>
                              <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                aria-label="Buscar partido o institución"
                                placeholder="Buscar partido o institución…"
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:w-64"
                              />
                            </div>
                            {visibleCards.length === 0 ? (
                              <p className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-500">
                                {cards.length === 0 ? "No hay partidos para asignar en este alcance." : "Ningún partido coincide con el filtro."}
                              </p>
                            ) : (
                              visibleCards.map((card) => <MatchCard key={card.idmatch} card={card} exclusiveStrategy={exclusiveStrategy} />)
                            )}
                          </section>
                        </>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="border-t border-slate-100 p-4">
                  {tab === TABS.VERSIONS && showVersions ? (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cerrar
                      </button>
                    </div>
                  ) : confirming ? (
                    <div className="space-y-3">
                      <p
                        className={`text-sm ${strong.length ? "font-bold text-rose-800" : conflicts.length || alternatePairs.length ? "font-bold text-amber-900" : "text-slate-700"}`}
                      >
                        {confirmation}
                      </p>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        {showVersions ? (
                          <span className="text-xs text-slate-500">Se aplicará en {versionTitle(versions.data.active)}.</span>
                        ) : (
                          <span />
                        )}
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setConfirming(false)}
                            disabled={applying}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={handleApply}
                            disabled={applying}
                            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:bg-indigo-400"
                          >
                            {applying ? "Aplicando…" : "Continuar"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={applying}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(true)}
                        disabled={!canApply}
                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Aplicar cambios ({writes})
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
